import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [validationState, setValidationState] = useState({
    email: null, // null, 'valid', 'invalid'
    password: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Real-time email validation
  const validateEmail = (email) => {
    if (!email?.trim()) {
      return { valid: false, message: 'Email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }
    return { valid: true, message: '' };
  };

  // Real-time password validation
  const validatePassword = (password) => {
    if (!password) {
      return { valid: false, message: 'Password is required' };
    }
    if (password?.length < 6) {
      return { valid: false, message: 'Password must be at least 6 characters' };
    }
    return { valid: true, message: '' };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (authError) {
      setAuthError(null);
    }

    // Real-time validation feedback
    if (field === 'email') {
      const validation = validateEmail(value);
      setValidationState(prev => ({ ...prev, email: validation?.valid ? 'valid' : 'invalid' }));
      if (!validation?.valid && value?.length > 0) {
        setErrors(prev => ({ ...prev, email: validation?.message }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }

    if (field === 'password') {
      const validation = validatePassword(value);
      setValidationState(prev => ({ ...prev, password: validation?.valid ? 'valid' : 'invalid' }));
      if (!validation?.valid && value?.length > 0) {
        setErrors(prev => ({ ...prev, password: validation?.message }));
      } else {
        setErrors(prev => ({ ...prev, password: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    const emailValidation = validateEmail(formData?.email);
    if (!emailValidation?.valid) {
      newErrors.email = emailValidation?.message;
      isValid = false;
    }

    const passwordValidation = validatePassword(formData?.password);
    if (!passwordValidation?.valid) {
      newErrors.password = passwordValidation?.message;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setAuthError(null);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      if (error) throw error;

      // Successful login - navigate to dashboard
      navigate("/dashboard");

    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific Supabase auth errors with user-friendly messages
      if (error?.message?.includes('Invalid login credentials')) {
        setAuthError('Invalid email or password. Please check your credentials and try again.');
        setErrors({ password: 'Invalid email or password' });
      } else if (error?.message?.includes('Email not confirmed')) {
        setAuthError('Please verify your email address before logging in.');
        setErrors({ email: 'Email not verified' });
        setShowResendEmail(true);
      } else if (error?.message?.includes('Too many requests')) {
        setAuthError('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (error?.message?.includes('Network')) {
        setAuthError('Network error. Please check your internet connection and try again.');
      } else {
        setAuthError(error?.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleResendVerification = async () => {
    if (!formData?.email) {
      setErrors({ email: 'Please enter your email address' });
      return;
    }

    setResendLoading(true);
    setErrors({});

    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: formData?.email
      });

      if (error) {
        setErrors({ email: error?.message || 'Failed to resend verification email' });
        setResendLoading(false);
        return;
      }

      setErrors({ email: 'Verification email sent! Please check your inbox.' });
      setResendLoading(false);
      
      setTimeout(() => {
        setShowResendEmail(false);
        setErrors({});
      }, 3000);
    } catch (err) {
      console.error('Resend verification error:', err);
      setErrors({ email: 'Failed to resend verification email' });
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4 overflow-hidden">
            <img 
              src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
              alt="DraftIQ Logo" 
              className="w-full h-full object-contain p-2"
            />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome to DraftIQ</h1>
          <p className="text-muted-foreground">Sign in to access your multi-agent business platform</p>
        </div>

        {/* Login Form Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          {/* Global Auth Error Message */}
          {authError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">{authError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field with Validation Indicator */}
            <div className="relative">
              <Input
                type="email"
                label="Email"
                placeholder="Enter your email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                required
                disabled={isLoading}
                className={cn(
                  validationState?.email === 'valid' && 'border-green-500 focus:border-green-500',
                  validationState?.email === 'invalid' && 'border-red-500 focus:border-red-500'
                )}
              />
              {/* Validation Icon */}
              {validationState?.email === 'valid' && (
                <CheckCircle2 className="absolute right-3 top-9 w-5 h-5 text-green-600" />
              )}
              {validationState?.email === 'invalid' && formData?.email?.length > 0 && (
                <AlertCircle className="absolute right-3 top-9 w-5 h-5 text-red-600" />
              )}
              {/* Inline Error Message */}
              {errors?.email && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors?.email}
                </p>
              )}
            </div>

            {/* Password Field with Validation Indicator */}
            <div className="relative">
              <Input
                type="password"
                label="Password"
                placeholder="Enter your password"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
                disabled={isLoading}
                className={cn(
                  validationState?.password === 'valid' && 'border-green-500 focus:border-green-500',
                  validationState?.password === 'invalid' && 'border-red-500 focus:border-red-500'
                )}
              />
              {/* Validation Icon */}
              {validationState?.password === 'valid' && (
                <CheckCircle2 className="absolute right-3 top-9 w-5 h-5 text-green-600" />
              )}
              {validationState?.password === 'invalid' && formData?.password?.length > 0 && (
                <AlertCircle className="absolute right-3 top-9 w-5 h-5 text-red-600" />
              )}
              {/* Inline Error Message */}
              {errors?.password && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors?.password}
                </p>
              )}
            </div>

            {/* Remember Me Checkbox */}
            <Checkbox
              id="rememberMe"
              checked={formData?.rememberMe}
              onChange={(e) => handleInputChange('rememberMe', e?.target?.checked)}
              label="Remember me"
              description="Stay signed in for 30 days"
              disabled={isLoading}
            />

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={isLoading}
              disabled={isLoading}
              className="mt-6"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            {/* Forgot Password Link */}
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isLoading}
                className={cn(
                  "text-sm text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors",
                  (isLoading) && "opacity-50 cursor-not-allowed"
                )}
              >
                Forgot your password?
              </button>
            </div>

            {/* Resend Email Verification */}
            {showResendEmail && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  Haven't received the verification email?
                </p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading || isLoading}
                  className="text-sm text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors font-medium"
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>Don't have an account? <button onClick={() => navigate('/register')} className="text-primary hover:underline">Sign up</button></p>
        </div>
      </div>
    </div>
  );
};

export default Login;